import React from 'react'
import { cloneDeep } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { useAppDispatch } from '../../../../../../hooks/redux'

import { NoteDB, MAX_LENGTH, formatAPINotes } from './helpers'
import {
  getUserNotes,
  postUserNote,
  putUserNote,
  deleteUserNote
} from '../../../../../../lib/api'
import { AddSuccessAlert } from '../../../../../../slices/alert'
import { useCustomerContext } from '../../../../../../providers/CustomerProvider'

type UseUserNotesOptions = {
  user?: APIUser
  modal?: boolean
}

const useUserNotes = ({ user, modal }: UseUserNotesOptions) => {
  const dispatch = useAppDispatch()

  const { saved, setSaved, notesToProvider } = useCustomerContext()

  const [notes, setNotes] = React.useState<NoteDB>({})
  const [loaded, setLoaded] = React.useState(false)

  // Formats notes for use with the api
  const apiNotes = React.useMemo(() => {
    if (!modal) {
      return []
    }

    const mapped: { id: string; content: string }[] = []
    Object.entries(notes).forEach(([id, { content }]) => {
      if (content.trim().length > 0) {
        const newNote = { id, content }
        mapped.push(newNote)
      }
    })
    return mapped
  }, [modal, notes])

  React.useEffect(() => {
    // Send notes to provider here, apiNotes should be formatted how the api wants them
    if (modal) {
      notesToProvider(apiNotes)
    }
  }, [modal, apiNotes, notesToProvider])

  // Loads notes from api
  React.useEffect(() => {
    if (!user || loaded) {
      return
    }

    let mounted = true

    try {
      const handleGetNotes = async () => {
        const notes = await getUserNotes(user.id)
        const formatted = formatAPINotes(notes)

        if (mounted) {
          setNotes(formatted)
          setLoaded(true)
        }
      }

      handleGetNotes()

      return () => {
        mounted = false
      }
    } catch (err) {
      console.error('Error getting notes', err)
    }
  }, [user, modal, loaded])

  // Resets notes state on save
  React.useEffect(() => {
    if (saved) {
      setNotes({})
      setSaved(false)
    }
  }, [saved, setSaved])

  // Creates a new note
  const postNote = React.useCallback(
    async (value: string) => {
      if (!value.length) {
        return
      }

      if (modal) {
        setNotes(old => {
          const id = uuidv4()
          return {
            ...old,
            [id]: {
              id,
              content: value,
              stamp: new Date().toISOString(),
              created_by: ''
            }
          }
        })
        return
      }

      try {
        const result = await postUserNote(user.id, {
          content: value.slice(0, MAX_LENGTH)
        })

        setNotes(old => ({
          ...old,
          ...formatAPINotes([result])
        }))
        dispatch(AddSuccessAlert('Note saved'))
      } catch (err) {
        console.error('Error getting notes', err)
      }
    },
    [modal, user, dispatch]
  )

  // Updates note locally and in api
  const putNote = React.useCallback(
    async (key: string, value: string) => {
      if (!value.length) {
        return
      }

      if (modal) {
        setNotes(old => ({ ...old, [key]: { ...old[key], content: value } }))
        return
      }

      // Ignore no content edits
      if (value === notes[key].content) {
        return
      }

      try {
        const result = await putUserNote(user.id, key, {
          content: value.slice(0, MAX_LENGTH)
        })

        setNotes(old => ({
          ...old,
          ...formatAPINotes([result])
        }))
        dispatch(AddSuccessAlert('Note saved'))
      } catch (err) {
        console.error('Error putting note', err)
      }
    },
    [modal, notes, user, dispatch]
  )

  // Remove note in api (if needed) and update locally
  const deleteNote = React.useCallback(
    async (key: string) => {
      const onFinish = () => {
        setNotes(old => {
          const copy = cloneDeep(old)
          delete copy[key]
          return copy
        })
      }

      if (modal) {
        onFinish()
        return
      }

      try {
        await deleteUserNote(user.id, key)
        onFinish()
        dispatch(AddSuccessAlert('Note deleted'))
      } catch (err) {
        console.error('Error deleting note', err)
      }
    },
    [modal, user, dispatch]
  )

  return { notes, postNote, putNote, deleteNote }
}

export default useUserNotes
